import axios from "axios";
import React, { useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import styled from "styled-components";

const ReportEditor = ({ reportId, report, onSubmit, onClose }) => {

    const convertedReport = useMemo(() => {
        function objectToKeyValueArray(array) {
            return array.map(item => ({
                key: item.note,
                value: item.amount
            }))
        }

        return {
            ...report,
            salary: objectToKeyValueArray(report.salary),
            salary_bonuce: objectToKeyValueArray(report.salary_bonuce),
            salary_advance: objectToKeyValueArray(report.salary_advance),
            debt: objectToKeyValueArray(report.debt),
            debt_bonuce: objectToKeyValueArray(report.debt_bonuce),
            debt_advance: objectToKeyValueArray(report.debt_advance),
        }
    }, [report])

    const { register, handleSubmit, control, watch } = useForm({
        defaultValues: convertedReport
    });

    const formValues = watch();

    const sum = useMemo(() => {

        function sumArray(array = []) {
            return array.reduce((sum, item) => sum + parseInt(item.value), 0) || 0;
        }

        const salary = sumArray(formValues.salary);
        const salary_bonuce = sumArray(formValues.salary_bonuce);
        const salary_advance = sumArray(formValues.salary_advance);
        const debt = sumArray(formValues.debt);
        const debt_bonuce = sumArray(formValues.debt_bonuce);
        const debt_advance = sumArray(formValues.debt_advance);

        return salary + salary_bonuce + salary_advance - debt - debt_bonuce - debt_advance;
    }, [formValues])

    async function onFormSubmit(formData) {

        function keyValueArrayToObject(array) {
            return array.map(item => ({
                note: item.key,
                amount: item.value
            }))
        }

        const newReport = {
            ...formData,
            salary: keyValueArrayToObject(formData.salary),
            salary_bonuce: keyValueArrayToObject(formData.salary_bonuce),
            salary_advance: keyValueArrayToObject(formData.salary_advance),
            debt: keyValueArrayToObject(formData.debt),
            debt_bonuce: keyValueArrayToObject(formData.debt_bonuce),
            debt_advance: keyValueArrayToObject(formData.debt_advance),
            sum
        }

        const { data } = await axios.post(`/api/wage-processor/report/${reportId}/update`, {
            report: newReport
        });

        onSubmit();
        onClose();
    }

    return (
        <StyledReportEditor onSubmit={handleSubmit(onFormSubmit)}>

            <InputList report={report} control={control} register={register} formValues={formValues}
                label='薪資加項'
                name='salary'
                sumColumn='value'
                columns={{
                    key: '項目',
                    value: '金額',
                }}
            />

            <InputList report={report} control={control} register={register} formValues={formValues}
                label='額外加項'
                name='salary_bonuce'
                sumColumn='value'
                columns={{
                    key: '項目',
                    value: '金額',
                }}
            />

            <InputList report={report} control={control} register={register} formValues={formValues}
                label='代墊款項加項'
                name='salary_advance'
                sumColumn='value'
                columns={{
                    key: '項目',
                    value: '金額',
                }}
            />

            <InputList report={report} control={control} register={register} formValues={formValues}
                label='薪資扣項'
                name='debt'
                sumColumn='value'
                columns={{
                    key: '項目',
                    value: '金額',
                }}
            />

            <InputList report={report} control={control} register={register} formValues={formValues}
                label='額外扣項'
                name='debt_bonuce'
                sumColumn='value'
                columns={{
                    key: '項目',
                    value: '金額',
                }}
            />

            <InputList report={report} control={control} register={register} formValues={formValues}
                label='代墊款項扣項'
                name='debt_advance'
                sumColumn='value'
                columns={{
                    key: '項目',
                    value: '金額',
                }}
            />


            <h4 style={{ textAlign: 'right' }}>實領金額計算：{sum}</h4>
            <h4>備註</h4>
            <textarea {...register('note')} />

            <InputList report={report} control={control} register={register} formValues={formValues}
                label='加班明細'
                name='overtimes' 
                sumColumn='amount'
                editable={false}
                columns={{
                    date: '日期',
                    is_holiday: '假日',
                    duration: '時數',
                    amount_formula: '加班費算式',
                    amount: '小結',
                }}
            />

            <InputList report={report} control={control} register={register} formValues={formValues}
                label='休假明細'
                name='dayoffs'
                sumColumn='amount'
                editable={false}
                columns={{
                    date: '日期',
                    duration: '時數',
                    dayoff_type: '假種',
                    amount_formula: '請假扣薪算式',
                    amount: '小結',
                }}
            />

            <div className="buttons">
                <button type='submit'>確定</button>
                <button type='button' onClick={onClose}>取消</button>
            </div>
        </StyledReportEditor>
    )
}

const InputList = ({ control, register, name, label, columns = {}, sumColumn, formValues, editable = true }) => {

    const { fields, append, remove} = useFieldArray({
        control, name,
    });

    function handleAdd() {
        const clearColumns = Object.keys(columns).reduce((obj, key) => {
            obj[key] = '';
            if (key === 'is_holiday') obj[key] = false;
            if (key === 'value' || key === 'amount') obj[key] = 0;
            return obj;
        }, {});
        append(clearColumns);
    }

    const sum = useMemo(() => {
        if (!sumColumn) {
            return null;
        }
        return formValues[name].reduce((sum, item) => sum + parseInt(item[sumColumn]), 0);
    }, [formValues, sumColumn, name])

    return (
        <StyledInputList>

            <h4>{ label }</h4>

            <table>
                <thead>
                    <tr>
                        { Object.entries(columns).map(([column, label]) =>
                            <th key={label}>{label}</th>
                        )}
                        { editable && <th></th> }
                    </tr>
                </thead>

                <tbody>
                { fields.map((key, index) => 
                    <tr key={index}>
                        { Object.entries(columns).map(([column, label]) =>
                            <td key={`${index}-${label}`}>
                                <input 
                                    disabled={!editable}
                                    type={ 
                                        (column === 'value' || column === 'amount') ? 'number' :
                                        column === 'is_holiday' ? 'checkbox' :
                                        column === 'date' ? 'date':
                                        'text' 
                                    } 
                                    {...register(`${name}.${index}.${column}`)} 
                                />
                            </td>
                        )}
                        { editable && 
                            <td>
                                <button type="button"  onClick={() => remove(index)}>-</button>
                            </td>
                        }
                    </tr>
                )}
                { sumColumn &&
                    <tr>
                        <td colSpan={Object.keys(columns).length-1}><b>總計</b></td>
                        <td><b>{sum}</b></td>
                        { editable && <td></td>}
                    </tr>
                }
                </tbody>
            </table>

            { editable &&
                <button className="add-button" type="button" onClick={handleAdd}>+</button>
            }
        </StyledInputList>
    )
}

const StyledReportEditor = styled.form`

    .buttons {
        margin-top: 24px;
        display: flex;
        justify-content: flex-end;
        gap: 12px;

        button {
            padding: 6px 12px;
        }
    }

    h4 {
        margin-top: 24px;
    }

    textarea {
        border-radius: 0;
        width: 100%;
        resize: vertical;
    }

    .hint {
        margin-top: 40px;
        border-top: 1px solid #ccc;
        padding-top: 8px;
    }
`

const StyledInputList = styled.div`

    table {
        width: 100%;
        background-color: white;
    }

    th, td {
        border: 1px solid #eee;
        padding: 4px 8px;
    }

    th {
        background-color: #ddd;
    }

    input {
        background-color: transparent;
        display: block;
        width: -webkit-fill-available;
        border-radius: 0;
        border: 0;
        padding: 0;
    }

    .add-button {
        display: block;
        margin-top: 8px;
        margin-left: auto;
    }
`

export default ReportEditor